/**
 * HTTP client for the Project-CAP API (integration/openapi.yaml).
 *
 * Contract handled here so callers never see it:
 * - Base URL from runtime config (apiGatewayUrl).
 * - `Authorization: Bearer <token>` from the stored session.
 * - Envelope unwrapping: success responses are `{ success, data }` — request()
 *   resolves with `data`. Errors are `{ success, error: { message,
 *   statusCode } }` — request() rejects with ApiError.
 * - One transparent refresh: on 401 with a stored refreshToken, POST
 *   /auth/refresh and retry the original request once; if that fails the
 *   session is cleared and onSessionExpired fires (AuthProvider logs out).
 */
import { getConfig } from './config';
import { readSession, writeSession, clearSession } from './session';
import { getActiveOrgId } from './orgState';

export class ApiError extends Error {
  constructor(message, statusCode, details) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.details = details;
  }
}

let onSessionExpired = null;

export function setSessionExpiredHandler(fn) {
  onSessionExpired = fn;
}

function baseUrl() {
  const url = getConfig().apiGatewayUrl;
  if (!url) {
    throw new ApiError(
      'No API endpoint configured (apiGatewayUrl is empty in config.json). ' +
        'The deploy pipeline fills this in once the API Gateway stack is live.',
      0
    );
  }
  return url.replace(/\/$/, '');
}

async function rawRequest(method, path, { body, query, token } = {}) {
  let url = baseUrl() + path;
  if (query) {
    const qs = new URLSearchParams(
      Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    if (qs) url += `?${qs}`;
  }
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  const orgId = getActiveOrgId();
  if (orgId) headers['X-Org-Id'] = orgId;

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    // Non-JSON body (e.g. gateway-level error page); fall through.
  }

  if (!response.ok || (payload && payload.success === false)) {
    const err = (payload && payload.error) || {};
    throw new ApiError(
      err.message || `Request failed (HTTP ${response.status})`,
      err.statusCode || response.status,
      err.details || err.validationErrors
    );
  }
  return payload && payload.data !== undefined ? payload.data : payload;
}

async function tryRefresh() {
  const session = readSession();
  if (!session || !session.refreshToken) return null;
  try {
    const data = await rawRequest('POST', '/auth/refresh', {
      body: { refreshToken: session.refreshToken }
    });
    const next = {
      ...session,
      token: data.accessToken || data.token, // FND-52: API returns accessToken
      refreshToken: data.refreshToken || session.refreshToken,
      expiresAt: data.expiresAt,
      user: data.user || session.user
    };
    writeSession(next);
    return next;
  } catch {
    return null;
  }
}

export async function request(method, path, options = {}) {
  const session = readSession();
  const token = options.auth === false ? undefined : session && session.token;
  try {
    return await rawRequest(method, path, { ...options, token });
  } catch (err) {
    const authFailed = err instanceof ApiError && err.statusCode === 401;
    if (authFailed && options.auth !== false && session) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return rawRequest(method, path, { ...options, token: refreshed.token });
      }
      clearSession();
      if (onSessionExpired) onSessionExpired();
    }
    throw err;
  }
}

export const get = (path, options) => request('GET', path, options);
export const post = (path, body, options) => request('POST', path, { ...options, body });
export const put = (path, body, options) => request('PUT', path, { ...options, body });
export const del = (path, options) => request('DELETE', path, options);
